import JSZip from 'jszip';
import { Hits } from './types';
import { formatStageLabel } from './stageLabel';

// ─── Shared drawing helpers ───────────────────────────────────────────────────

const W = 900;
const H = 420;
const RADIUS = 18;
const PAD = 32;

const COLORS = {
  bg: 'rgba(15, 17, 22, 0.50)',
  accent: '#f0c040',
  text: '#ffffff',
  muted: '#9ba3b4',
  divider: 'rgba(255,255,255,0.10)',
  highlight: 'rgba(240,192,64,0.12)',
  green: '#4cce7a',
  red: '#f05060',
  rowAlt: 'rgba(255,255,255,0.04)',
};

// Stage title bar — shared by both overlay images so they stay in lockstep.
// The title size is fixed rather than shrink-to-fit: a constant size keeps the
// header reading the same across every stage in an exported sequence, and a
// long stage name is ellipsised instead.
const HEADER_FONT = 'bold 44px system-ui, sans-serif';
const HEADER_ASIDE_FONT = 'bold 40px system-ui, sans-serif';
const HEADER_BASELINE = PAD + 36;
const HEADER_DIVIDER_Y = PAD + 56;
/** Gap between the title and whatever shares its row on the right. */
const HEADER_GAP = 24;

/**
 * `text` trimmed to fit `maxWidth`, with a trailing ellipsis if it was cut.
 *
 * Stage names are free text from the match config and run long ("12 — The Long
 * Walk Home Again"), which at 44px overruns the panel. Assumes `ctx.font` is
 * already the font the text will be drawn in.
 */
function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;

  let cut = text.length;
  while (cut > 0 && ctx.measureText(`${text.slice(0, cut)}…`).width > maxWidth) cut--;
  return `${text.slice(0, cut).trimEnd()}…`;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawBackground(ctx: CanvasRenderingContext2D): void {
  const { width, height } = ctx.canvas;
  ctx.clearRect(0, 0, width, height);
  roundRect(ctx, 0, 0, width, height, RADIUS);
  ctx.fillStyle = COLORS.bg;
  ctx.fill();
}

function drawDivider(ctx: CanvasRenderingContext2D, y: number): void {
  ctx.strokeStyle = COLORS.divider;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PAD, y);
  ctx.lineTo(W - PAD, y);
  ctx.stroke();
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Canvas toBlob returned null'));
    }, 'image/png');
  });
}

function triggerDownload(canvas: HTMLCanvasElement, filename: string): void {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, 'image/png');
}

function sanitiseFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '_');
}

// ─── Image 1: Stage Result ────────────────────────────────────────────────────

export interface StageResultParams {
  stageNumber: number;
  /** Raw stage name (may be absent); combined with the number by {@link formatStageLabel}. */
  stageName?: string;
  hitFactor: number;
  time: number;
  stageScore: number;
  maxPossibleScore: number;
  hits: Hits;
  procedures: number;
  stagePercent: string;
  /** Rank on this stage alone (1 = stage win), not the cumulative standing. */
  placement: number;
  /** Competitors who have shot this stage, i.e. the field `placement` is out of. */
  totalOnStage: number;
}

export function buildStageResultCanvas(params: StageResultParams): HTMLCanvasElement {
  // ── Compute layout constants before creating the canvas ──────────
  const labelY    = HEADER_DIVIDER_Y + 32;
  const valY      = labelY + 62;
  const hitLabelY = valY + 66;
  const hitValY   = hitLabelY + 58;       // room for the 52px hit values
  const canvasH   = hitValY + 16 + PAD;   // bottom of 52px text + padding

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d')!;

  drawBackground(ctx);

  // ── Header ──────────────────────────────────────────────────────
  // Stage placement sits in the same right-hand header slot the standings
  // image uses for its competitor count, and is drawn first so its measured
  // width sets the width the title is ellipsised to.
  const placeText = `#${params.placement} / ${params.totalOnStage}`;
  ctx.fillStyle = COLORS.text;
  ctx.font = HEADER_ASIDE_FONT;
  ctx.textAlign = 'right';
  ctx.fillText(placeText, W - PAD, HEADER_BASELINE);
  ctx.textAlign = 'left';
  const placeWidth = ctx.measureText(placeText).width;

  const title = formatStageLabel(params.stageNumber, params.stageName);
  ctx.fillStyle = COLORS.accent;
  ctx.font = HEADER_FONT;
  ctx.fillText(
    ellipsize(ctx, title, W - PAD * 2 - placeWidth - HEADER_GAP),
    PAD,
    HEADER_BASELINE,
  );

  drawDivider(ctx, HEADER_DIVIDER_Y);

  // ── Metrics labels row ───────────────────────────────────────────
  // All three values share the 58px size, so the columns are spaced wide
  // enough for the longest each can get ("114.83s", "100.0%").
  const metricX = [PAD, PAD + 300, PAD + 580];

  ctx.fillStyle = COLORS.muted;
  ctx.font = '20px system-ui, sans-serif';
  ctx.fillText('HIT FACTOR', metricX[0], labelY);
  ctx.fillText('TIME', metricX[1], labelY);
  ctx.fillText('SCORE', metricX[2], labelY);

  // ── Metric values row (all 58px bold, baseline valY) ─────────────
  ctx.font = 'bold 58px system-ui, sans-serif';

  ctx.fillStyle = COLORS.accent;
  ctx.fillText(params.hitFactor.toFixed(2), metricX[0], valY);

  ctx.fillStyle = COLORS.text;
  ctx.fillText(`${params.time.toFixed(2)}s`, metricX[1], valY);

  ctx.fillStyle = COLORS.accent;
  ctx.fillText(`${params.stagePercent}%`, metricX[2], valY);

  drawDivider(ctx, valY + 28);

  // ── Hits breakdown ───────────────────────────────────────────────

  const hitsItems: [string, number][] = [
    ['A', params.hits.A],
    ['C', params.hits.C],
    ['D', params.hits.D],
    ['M', params.hits.M],
    ['NS', params.hits.NS],
    ['Proc', params.procedures],
  ];
  const colWidth = (W - PAD * 2) / hitsItems.length;

  hitsItems.forEach(([label, value], i) => {
    const x = PAD + i * colWidth;
    ctx.fillStyle = COLORS.muted;
    ctx.font = '20px system-ui, sans-serif';
    ctx.fillText(label, x, hitLabelY);
    ctx.fillStyle = value > 0 && (label === 'M' || label === 'NS' || label === 'Proc')
      ? COLORS.red
      : COLORS.text;
    ctx.font = 'bold 52px system-ui, sans-serif';
    ctx.fillText(String(value), x, hitValY);
  });

  return canvas;
}

export function downloadStageResultImage(params: StageResultParams): void {
  const filename = `${sanitiseFilename(formatStageLabel(params.stageNumber, params.stageName))}_result.png`;
  triggerDownload(buildStageResultCanvas(params), filename);
}

export function stageResultToBlob(params: StageResultParams): Promise<Blob> {
  return canvasToBlob(buildStageResultCanvas(params));
}

// ─── Image 2: Virtual Standings ───────────────────────────────────────────────

export type Movement = 'up' | 'down' | 'none';

export interface StandingRow {
  rank: number;
  name: string;
  scorePercent: number;
  isShooter: boolean;
}

export interface StandingsParams {
  stageNumber: number;
  /** Raw stage name (may be absent); combined with the number by {@link formatStageLabel}. */
  stageName?: string;
  rows: StandingRow[];
  movement: Movement;
  shooterTotalScore: number;
  totalCompetitors: number;
}

function drawMovementArrow(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  movement: Movement,
): void {
  if (movement === 'none') return;

  const size = 16;
  ctx.beginPath();
  if (movement === 'up') {
    ctx.moveTo(x, y - size);
    ctx.lineTo(x + size, y + size / 2);
    ctx.lineTo(x - size, y + size / 2);
    ctx.fillStyle = COLORS.green;
  } else {
    ctx.moveTo(x, y + size);
    ctx.lineTo(x + size, y - size / 2);
    ctx.lineTo(x - size, y - size / 2);
    ctx.fillStyle = COLORS.red;
  }
  ctx.closePath();
  ctx.fill();
}

export function buildStandingsCanvas(params: StandingsParams): HTMLCanvasElement {
  // ── Rows ─────────────────────────────────────────────────────────
  const rowH = 76;
  const startY = HEADER_DIVIDER_Y + 12;
  // Grow with the row count so taller rows never spill off the bottom.
  const canvasH = Math.max(H, startY + params.rows.length * rowH + PAD);

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = canvasH;
  const ctx = canvas.getContext('2d')!;

  drawBackground(ctx);

  // ── Header ──────────────────────────────────────────────────────
  // Right-aligned so a four-digit field can't run off the panel edge. Drawn
  // first so its measured width sets the width the title is ellipsised to.
  const countText = `${params.totalCompetitors} competitors`;
  ctx.fillStyle = COLORS.muted;
  ctx.font = '22px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillText(countText, W - PAD, HEADER_BASELINE);
  ctx.textAlign = 'left';
  const countWidth = ctx.measureText(countText).width;

  const title = `Standings after ${formatStageLabel(params.stageNumber, params.stageName)}`;
  ctx.fillStyle = COLORS.accent;
  ctx.font = HEADER_FONT;
  ctx.fillText(
    ellipsize(ctx, title, W - PAD * 2 - countWidth - HEADER_GAP),
    PAD,
    HEADER_BASELINE,
  );

  drawDivider(ctx, HEADER_DIVIDER_Y);

  params.rows.forEach((row, i) => {
    const ry = startY + i * rowH;
    // Baseline that keeps the enlarged rank/name text optically centred.
    const baseline = ry + rowH / 2 + 12;

    if (row.isShooter) {
      roundRect(ctx, PAD - 8, ry - 2, W - PAD * 2 + 16, rowH - 4, 8);
      ctx.fillStyle = COLORS.highlight;
      ctx.fill();
    } else if (i % 2 === 0) {
      roundRect(ctx, PAD - 8, ry - 2, W - PAD * 2 + 16, rowH - 4, 8);
      ctx.fillStyle = COLORS.rowAlt;
      ctx.fill();
    }

    // Rank
    ctx.fillStyle = row.isShooter ? COLORS.accent : COLORS.muted;
    ctx.font = row.isShooter ? 'bold 40px system-ui, sans-serif' : 'bold 34px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`#${row.rank}`, PAD + 92, baseline);
    ctx.textAlign = 'left';

    // Name
    ctx.fillStyle = row.isShooter ? COLORS.text : COLORS.muted;
    ctx.font = row.isShooter ? 'bold 28px system-ui, sans-serif' : '26px system-ui, sans-serif';
    ctx.fillText(row.name, PAD + 112, baseline);

    // Score percent (right-aligned)
    const pctStr = `${row.scorePercent.toFixed(1)}%`;
    ctx.fillStyle = row.isShooter ? COLORS.accent : COLORS.muted;
    ctx.font = row.isShooter ? 'bold 26px system-ui, sans-serif' : '24px system-ui, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(pctStr, W - PAD - (row.isShooter ? 56 : 8), baseline);
    ctx.textAlign = 'left';

    // Movement arrow — only on shooter row
    if (row.isShooter) {
      drawMovementArrow(ctx, W - PAD - 22, ry + rowH / 2, params.movement);
    }
  });

  return canvas;
}

export function downloadStandingsImage(params: StandingsParams): void {
  const filename = `${sanitiseFilename(formatStageLabel(params.stageNumber, params.stageName))}_standings.png`;
  triggerDownload(buildStandingsCanvas(params), filename);
}

export function standingsToBlob(params: StandingsParams): Promise<Blob> {
  return canvasToBlob(buildStandingsCanvas(params));
}

// ─── Zip all stages for one competitor ───────────────────────────────────────

export interface StageOverlayEntry {
  stageResultParams: StageResultParams;
  standingsParams: StandingsParams;
  filePrefix: string;   // e.g. "01-Stage_5"
}

export async function downloadAllOverlaysAsZip(
  competitorName: string,
  entries: StageOverlayEntry[],
): Promise<void> {
  const zip = new JSZip();
  const folder = zip.folder(sanitiseFilename(competitorName))!;

  await Promise.all(
    entries.map(async ({ stageResultParams, standingsParams, filePrefix }) => {
      const [resultBlob, standingsBlob] = await Promise.all([
        stageResultToBlob(stageResultParams),
        standingsToBlob(standingsParams),
      ]);
      folder.file(`${filePrefix}_result.png`, resultBlob);
      folder.file(`${filePrefix}_standings.png`, standingsBlob);
    }),
  );

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const url = URL.createObjectURL(zipBlob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${sanitiseFilename(competitorName)}_overlays.zip`;
  a.click();
  URL.revokeObjectURL(url);
}
